/** leads/LeadPortalInfoSheet.tsx — 법인 정보 확인 시트 (리드포털 v4, 한화 법인DB 앱 레이아웃 차용 2026-09-05)
 *  상단 × 헤더 · 3섹션(법인 DB 정보 / 유형별 정보(경정청구·인증) / 요약재무제표 단위:만원) · 하단 확인 CTA.
 *  '미팅 정보' 자리에 우리 데이터(경정청구·인증 만료 정보)를 넣는다 — 사용자 확정. 재무제표는 열 때 비동기 조회. */
import React, { useEffect, useState } from 'react';
import { portalFsSummary } from './api';
import type { PortalLead, FsSummary } from './types';
import { fmtAmount, fmtPhone, telHref } from './calc';
import { C, tdef, certPeriod, expiryOf, newDeadlineOf, dday, ddayText, ddayStyle, manwon } from './portalFmt';

interface Props { lead: PortalLead; token: string; onClose: () => void; onToast: (m: string) => void }

function Row({ k, children }: { k: string; children: React.ReactNode }) {
  return <div className="lp4-row"><span>{k}</span><div>{children}</div></div>;
}
/** D-day 칩 — 잔여 1년 초과는 표시 안 함(경고 피로 방지). 지난 날짜는 접두어 없이 '만료됨' */
function DChip({ d, prefix }: { d: number | null; prefix?: string }) {
  if (d == null || d > 365) return null;
  const s = ddayStyle(d);
  return <span className="lp4-dchip" style={{ color: s.fg, background: s.bg }}>{(d < 0 ? '' : (prefix || '')) + ddayText(d)}</span>;
}

/** 요약재무제표·요약손익계산서 — 앱 원본 형태(2개년 · 단위 만원 · 4행/3행) 그대로.
 *  값 출처(실측 2026-09-05): 자산·부채·자본·매출액·영업이익·당기순이익 = 요약 API, 이익잉여금 = 재무상태표 API. (원본의 매출원가는 API 미제공 → 영업이익으로 대체) */
type FsYear = FsSummary['years'][number];
interface FsRowDef { label: string; key: keyof Omit<FsYear, 'year' | 'bas_dt'> }
const FS_TABLES: { title: string; rows: FsRowDef[] }[] = [
  { title: '요약재무제표', rows: [{ label: '자산', key: 'asset' }, { label: '부채', key: 'debt' }, { label: '자본', key: 'equity' }, { label: '이익잉여금', key: 'retained' }] },
  { title: '요약손익계산서', rows: [{ label: '매출액', key: 'sale' }, { label: '영업이익', key: 'op' }, { label: '당기순이익', key: 'np' }] },
];
/** 결산 기준일 → 'YY년 MM월' (basDt 없으면 'YY년') */
function colHead(y: FsYear): string {
  const m = String(y.bas_dt || '').match(/^(\d{4})(\d{2})/);
  return m ? m[1].slice(2) + '년 ' + String(+m[2]) + '월' : String(y.year).slice(2) + '년';
}

export default function LeadPortalInfoSheet({ lead: l, token, onClose, onToast }: Props) {
  const [fs, setFs] = useState<FsSummary | null>(null);
  const [fsErr, setFsErr] = useState('');
  const [copied, setCopied] = useState(false);
  const t = tdef(l.lead_type);
  const cp = certPeriod(l);
  const exp = expiryOf(l);

  useEffect(() => { // 열 때 1회 조회 — 실패해도 시트는 정상 표시 (안내 문구)
    let alive = true;
    if (!l.mine) { setFs({ years: [], increases: [], reason: 'no_corp_no' }); return undefined; }
    portalFsSummary(token, l.id).then((d) => { if (alive) setFs(d); }).catch((e) => { if (alive) { setFs({ years: [], increases: [], reason: 'none' }); setFsErr((e as Error).message); } });
    return () => { alive = false; };
  }, [token, l.id, l.mine]);

  const latest = fs && fs.years.length ? fs.years[fs.years.length - 1] : null;
  const cols = fs ? fs.years.slice(-2) : []; // 최신 2개년 (앱 원본: 24년 12월 · 25년 12월)
  // 최신 2개년이 연속이 아니면(예: 22년 · 25년) 사이 연도는 공시 자료가 없는 것 — 표 아래에 이유를 적어 준다 (사용자 피드백 2026-09-11 「22년 다음이 25년?」)
  const gapYears: number[] = cols.length === 2 ? Array.from({ length: Math.max(0, cols[1].year - cols[0].year - 1) }, (_, i) => cols[0].year + 1 + i) : [];
  // 경정청구 귀속연도 = 종합소득세 신고(다음 해 5월 31일)가 끝난 연도만 — 아직 신고 전인 연도는 경정청구 대상이 아니므로 표시하지 않는다 (사용자 지시 2026-09-11)
  const now = new Date();
  const claimYears = (l.detail?.eligible_years || []).filter((y) => new Date(y.year + 1, 4, 31, 23, 59, 59) < now);
  const firstFilable = (l.detail?.eligible_years || []).map((y) => y.year).filter((y) => new Date(y + 1, 4, 31, 23, 59, 59) >= now).sort((a, b) => a - b)[0];
  // 단위 만원 (원본 동일). 미제공(null)·0 은 '정보없음' 회색 — 사용자 지시 2026-09-05 (0은 공시 누락으로 보는 게 실무상 맞음)
  const cell = (v: number | null | undefined) => (v == null || isNaN(v) || v === 0 ? <span className="lp4-na">정보없음</span> : Math.round(v / 10000).toLocaleString());
  const copyAddr = async () => {
    try { await navigator.clipboard.writeText(l.address); setCopied(true); window.setTimeout(() => setCopied(false), 1500); } catch { onToast('복사 실패'); }
  };

  return (
    <div className="lp4-ov" onClick={onClose}>
      <div className="lp4-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="법인 정보 확인">
        <div className="lp4-sheetHead"><b>법인 정보 확인</b><button className="lp4-x" onClick={onClose} aria-label="닫기">×</button></div>
        <div className="lp4-sheetBody">
          {/* ① 법인 DB 정보 */}
          <div className="lp4-sec">
            <div className="lp4-secT">법인 DB 정보</div>
            <Row k="업체명">{l.corp_name}</Row>
            <Row k="대표자">{l.ceo_name || <span className="lp4-na">정보없음</span>}</Row>
            <Row k="업종">{l.detail?.industry || <span className="lp4-na">정보없음</span>}</Row>
            <Row k="사업자 번호"><span className="num">{l.biz_no || '-'}</span></Row>
            <Row k="회사 주소">
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>{l.address || <span className="lp4-na">정보없음</span>}</div>
                {l.address && <button className="lp4-mini" onClick={copyAddr}>{copied ? '복사됨 ✓' : '주소복사'}</button>}
              </div>
              {l.address && <a className="lp4-maplink" href={'https://map.kakao.com/link/search/' + encodeURIComponent(l.address)} target="_blank" rel="noreferrer">지도에서 보기 ›</a>}
            </Row>
            {/* 전화번호 — 국가번호 없이 · 하이픈 표기 (fmtPhone, 2026-09-21) */}
            <Row k="전화번호">{l.phone
              ? <a href={telHref(l.phone)} className="lp4-tel">{fmtPhone(l.phone)}</a>
              : <span className="lp4-hl">연락처 미확보 — 방문 후 미팅 결과에 남겨주세요</span>}</Row>
            <Row k="당기순이익">{fs == null ? <span style={{ color: C.sub }}>조회 중…</span> : latest && latest.np ? <><span className="num">{manwon(latest.np)}</span><span className="lp4-unit"> 만원 ({latest.year}년)</span></> : <span className="lp4-na">정보없음</span>}</Row>
          </div>

          {/* ② 유형별 정보 — 경정청구 / 인증 만료 */}
          <div className="lp4-sec">
            <div className="lp4-secT" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {l.lead_type === 'venture_new' ? '벤처확인 · 소득공제 정보' : t.cert ? t.label + ' 인증 정보' : '벤처투자소득공제 경정청구 정보'}{/* 제목 문구 — 사용자 지시 2026-09-05 (유형 칩 제거) */}
            </div>
            {l.lead_type === 'venture_new' ? (<>
              {/* 신규법인 (2026-10-17) — 설립 출자금은 회사가 기한 안에 벤처확인을 받으면 벤처투자 소득공제 대상 */}
              <Row k="설립연도">{l.detail?.estb_year ? l.detail.estb_year + '년' : <span className="lp4-na">정보없음</span>}</Row>
              <Row k="벤처확인 기한">{newDeadlineOf(l) ? <><span className="num">{newDeadlineOf(l)}</span> <DChip d={dday(newDeadlineOf(l))} /></> : <span className="lp4-na">정보없음</span>}</Row>
              <div className="lp4-note">ⓘ 법인 설립 때 대표님이 납입한 자본금은 벤처기업 출자에 해당합니다. 회사가 기한 안에 벤처기업 확인을 받으면, 출자한 해부터 2년 뒤 해까지 중 한 해의 종합소득에서 출자금 소득공제를 받을 수 있습니다. 카드의 「제안서 열기」로 대표님께 드릴 안내 DM을 볼 수 있습니다.</div>
            </>) : t.cert ? (<>
              <Row k="인증 구분">{l.lead_type === 'venture_expiry' ? (l.detail?.confirm_type || '벤처기업 확인') : t.label + ' 인증'}</Row>
              {cp && <Row k="유효기간"><span className="num" style={{ whiteSpace: 'nowrap' }}>{cp.text}</span>{cp.derived ? <span className="lp4-derived" title="원본 명단에 인증 시작일이 없어 만료일에서 역산한 값입니다">{cp.derived}년 기준</span> : null}</Row>}
              <Row k="만료일">{exp ? <><span className="num">{exp}</span> <DChip d={dday(exp)} /></> : <span className="lp4-na">정보없음</span>}</Row>
              {/* 재확인 신청 시기 — 연장 제안서와 같은 기준 (2026-09-19) */}
              <Row k="재확인 신청">{exp ? ((dday(exp) ?? 0) < 0 ? <span className="lp4-hl">지금 — 만료 후 재신청 (심사 기간 우대 공백)</span> : <span>지금 — 만료 전 신청 · 심사 약 4~6주</span>) : <span className="lp4-na">정보없음</span>}</Row>
              <div className="lp4-note">{exp && (dday(exp) ?? 0) < 0
                ? 'ⓘ 이미 만료된 인증입니다. 재신청 심사 기간 동안 보증·금융 우대가 중단되므로 바로 안내해 주세요. 카드의 「제안서 열기」로 대표님께 드릴 안내 DM을 볼 수 있습니다.'
                : 'ⓘ 만료 전 재인증(갱신) 신청이 필요합니다. 만료 후에는 세제·금융 우대가 중단될 수 있어 미리 안내해 주세요. 카드의 「제안서 열기」로 대표님께 드릴 안내 DM을 볼 수 있습니다.'}</div>
            </>) : (<>
              <Row k="투자연도">{l.detail?.invest_year ? l.detail.invest_year + '년' : <span className="lp4-na">정보없음</span>}</Row>
              <Row k="투자금액"><b style={{ fontWeight: 800 }}>{fmtAmount(l.detail?.invest_amount)}{l.detail?.invest_amount != null ? '원' : ''}</b></Row>
              {cp && <Row k="벤처 유효기간"><span className="num" style={{ whiteSpace: 'nowrap' }}>{cp.text}</span> {cp.to ? <DChip d={dday(cp.to)} prefix="만료 " /> : null}</Row>}
              <Row k="경정청구 귀속연도">
                {/* 귀속연도만 글자로 — 마감일·칩 없이 (사용자 지시 2026-09-05) · 신고가 끝난 연도만 (2026-09-11) */}
                {(l.detail?.eligible_years || []).length === 0
                  ? <span className="lp4-na">정보없음</span>
                  : claimYears.length === 0
                    ? <span className="lp4-na">아직 없음</span>
                    : <span className="num">{claimYears.map((y) => y.year + '년').join(' · ')}</span>}
              </Row>
              <div className="lp4-note">
                {claimYears.length > 0
                  ? 'ⓘ 표시된 귀속연도의 종합소득에서 투자금 소득공제를 경정청구로 환급받을 수 있습니다.'
                  : firstFilable
                    ? `ⓘ ${firstFilable}년 귀속 종합소득세 신고(${firstFilable + 1}년 5월)가 끝나야 경정청구할 수 있습니다. 그 전에는 정기 신고에서 공제받습니다.`
                    : 'ⓘ 경정청구할 수 있는 귀속연도가 없습니다.'}
              </div>
            </>)}
          </div>

          {/* ③ 요약재무제표 · 요약손익계산서 (단위:만원) — 앱 원본 표 형태 */}
          {fs == null ? (
            <div className="lp4-fsT"><b>요약재무제표</b><span>(단위:만원)</span></div>
          ) : fs.years.length === 0 ? (<>
            <div className="lp4-fsT"><b>요약재무제표</b><span>(단위:만원)</span></div>
            <div className="lp4-fsmsg">{fs.reason === 'no_corp_no' ? '법인등록번호를 확인할 수 없어 재무정보를 조회하지 못했습니다.' : (fsErr || '공시 재무정보가 없는 법인입니다 (외부감사 대상 아님 가능). 가결산 자료로 안내해 주세요.')}</div>
          </>) : FS_TABLES.map((tb) => (
            <div key={tb.title} className="lp4-fsBlock">
              <div className="lp4-fsT"><b>{tb.title}</b><span>(단위:만원)</span></div>
              <table className="lp4-fs">
                <thead><tr><th>계정명</th>{cols.map((y) => <th key={y.year}>{colHead(y)}</th>)}</tr></thead>
                <tbody>
                  {tb.rows.map((r) => (
                    <tr key={r.label}><td>{r.label}</td>{cols.map((y) => <td key={y.year}>{cell(y[r.key])}</td>)}</tr>
                  ))}
                </tbody>
              </table>
              {tb === FS_TABLES[FS_TABLES.length - 1] && gapYears.length > 0 && (
                <div className="lp4-fsgap">ⓘ {gapYears.map((y) => String(y).slice(2) + '년').join('·')} 결산은 공시 자료가 없어, 공시된 최근 2개년({cols.map((y) => String(y.year).slice(2) + '년').join('·')})을 표시합니다.</div>
              )}
            </div>
          ))}
          {fs == null && <div className="lp4-fsmsg">재무정보를 불러오는 중…</div>}
        </div>
        <div className="lp4-sheetFoot"><button className="lp4-cta" onClick={onClose}>확인</button></div>
      </div>
    </div>
  );
}
